import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaSnowflake,
  FaSearch,
  FaCalendarCheck,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaUserFriends,
  FaClock,
  FaCity,
  FaArrowLeft,
  FaTools,
  FaPlus,
  FaMinus,
  FaCheckCircle,
  FaTimesCircle,
  FaChevronLeft,
  FaChevronRight,
  FaUser,
  FaStar,
  FaTimes,
  FaCommentDots,
  FaArrowRight,
} from "react-icons/fa";
import config, { getUserCity } from "../../../config";
import { useCityPrice } from "../../../hooks/useCityPrice";

const fmtTime = (qty) => {
  const mins = qty * 30;
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
};

const AcRepair = () => {
  const navigate = useNavigate();
  const { cityPrice } = useCityPrice();

  const [activeForm, setActiveForm] = useState("schedule");
  const [imageIndexes, setImageIndexes] = useState({});
  const [selectedServices, setSelectedServices] = useState([]);

  const [searchCriteria, setSearchCriteria] = useState({
	gender: "",
	city: "",
	area: "",
  });

  const [labourResults, setLabourResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");

  const [scheduleData, setScheduleData] = useState({
	name: "",
	date: "",
	time: "",
	slot: "",
	city: "",
	area: "",
	mobile: "",
	address: "",
	notes: "",
  });

  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");
  const [feedbackData, setFeedbackData] = useState([]);
  const [activeFeedbackService, setActiveFeedbackService] = useState(null);

  const BASE_URL = config.API_BASE_URL;
  const categoryKey = config.TECHNICAL_SERVICE_CATEGORY;
  const serviceKey = config.AC_SERVICE;
  const areaOptions = config.AREA_OPTIONS || {};

  const selectedCategory = config.SERVICE_CONFIG?.[categoryKey];
  const selectedService =
	config.SERVICE_CONFIG?.[categoryKey]?.services?.[serviceKey];
  const serviceTypes = selectedService?.types || [];

  const serviceLabel = selectedService?.label || "AC Repair";
  const categoryLabel = selectedCategory?.label || "Technical Services";
  const scheduleDaysLimit = config.SCHEDULE_DAYS_LIMIT ?? 3;

  const slotStartHour = config.SLOT_START_HOUR ?? 6;
  const slotEndHour = config.SLOT_END_HOUR ?? 20;
  const slotDurationHours = config.SLOT_DURATION_HOURS ?? 2;

  const defaultAcImage = "/img/services/ac/default.jpg";

  const normalizeImages = (imageValue, value) => {
	if (Array.isArray(imageValue)) {
	  const validImages = imageValue.filter(Boolean);
	  if (validImages.length > 0) return validImages;
	}

	if (typeof imageValue === "string" && imageValue.trim()) {
	  return [imageValue];
	}

	const val = value?.toLowerCase() || "";

	if (val.includes("gas")) return ["/img/services/ac/gas-refill.jpg"];
	if (val.includes("install")) return ["/img/services/ac/installation.jpg"];
	if (val.includes("repair")) return ["/img/services/ac/repair.jpg"];
	if (val.includes("service")) return ["/img/services/ac/service.jpg"];

	return [defaultAcImage];
  };

  const enhancedServices = useMemo(() => {
	return serviceTypes.map((service) => ({
	  ...service,
	  images: normalizeImages(service.image, service.value),
	  dos: service.includes || service.dos || [
		"Inspection and diagnosis",
		"Cleaning and service support",
		"Basic AC performance check",
	  ],
	  donts: service.excludes || service.donts || [
		"No wall breaking",
		"No electrical rewiring",
		"No spare parts unless mentioned",
	  ],
	  unit: service.unit || "unit",
	  limit: service.limit || 10,
	  price: service.price || 0,
	}));
  }, [serviceTypes]);

  const getSelectedService = (value) => {
	return selectedServices.find((service) => service.value === value);
  };

  const addService = (service) => {
	const exists = selectedServices.find((item) => item.value === service.value);

	if (exists) {
	  setSelectedServices((prev) =>
		prev.map((item) =>
		  item.value === service.value
			? {
				...item,
				quantity: Math.min(
				  (item.quantity || 1) + 1,
				  service.limit || 10
				),
			  }
			: item
		)
	  );
	} else {
	  setSelectedServices((prev) => [...prev, { ...service, quantity: 1 }]);
	}
  };

  const updateQty = (value, delta, limit) => {
	setSelectedServices((prev) =>
	  prev
		.map((item) => {
		  if (item.value !== value) return item;

		  const nextQty = Math.max(
			0,
			Math.min((item.quantity || 0) + delta, limit || 10)
		  );

		  return { ...item, quantity: nextQty };
		})
		.filter((item) => item.quantity > 0)
	);
  };

  const total = useMemo(() => {
	return selectedServices.reduce(
	  (sum, service) => sum + cityPrice(service.value, service.price) * service.quantity,
	  0
	);
  }, [selectedServices, cityPrice]);

  const totalItems = useMemo(() => {
	return selectedServices.reduce((sum, service) => sum + service.quantity, 0);
  }, [selectedServices]);

  const formatHourLabel = (hour) => {
	const suffix = hour >= 12 ? "pm" : "am";
	const normalized = hour % 12 === 0 ? 12 : hour % 12;
	return `${normalized}${suffix}`;
  };

  const generateTimeSlots = () => {
	const slots = [];
	let count = 1;

	for (
	  let start = slotStartHour;
	  start + slotDurationHours <= slotEndHour;
	  start += slotDurationHours
	) {
	  const end = start + slotDurationHours;

	  slots.push({
		slot: `slot${count}`,
		label: `${formatHourLabel(start)}-${formatHourLabel(end)}`,
	  });

	  count += 1;
	}

	return slots;
  };

  const timeSlots = generateTimeSlots();

  const getCityAreas = (city) => {
	if (!city) return [];
	const cityKey = city.toLowerCase().trim();
	const cityConfig = areaOptions?.[cityKey];
	return Array.isArray(cityConfig?.areas) ? cityConfig.areas : [];
  };

  const getCityLimit = (city) => {
	if (!city) return null;
	const cityKey = city.toLowerCase().trim();
	return areaOptions?.[cityKey]?.limit ?? null;
  };

  const getCurrentImageIndex = (serviceValue, imagesLength) => {
	const index = imageIndexes[serviceValue] || 0;
	if (index >= imagesLength) return 0;
	return index;
  };

  const goToPrevImage = (serviceValue, imagesLength) => {
	setImageIndexes((prev) => {
	  const currentIndex = prev[serviceValue] || 0;
	  const nextIndex = currentIndex === 0 ? imagesLength - 1 : currentIndex - 1;
	  return {
		...prev,
		[serviceValue]: nextIndex,
	  };
	});
  };

  const goToNextImage = (serviceValue, imagesLength) => {
	setImageIndexes((prev) => {
	  const currentIndex = prev[serviceValue] || 0;
	  const nextIndex = currentIndex === imagesLength - 1 ? 0 : currentIndex + 1;
	  return {
		...prev,
		[serviceValue]: nextIndex,
	  };
	});
  };

  const setImageAtIndex = (serviceValue, index) => {
	setImageIndexes((prev) => ({
	  ...prev,
	  [serviceValue]: index,
	}));
  };

  const handleTimeSlotChange = (e) => {
	const selectedSlotKey = e.target.value;
	const selectedSlot = timeSlots.find((item) => item.slot === selectedSlotKey);

	setScheduleData((prev) => ({
	  ...prev,
	  slot: selectedSlot?.slot || "",
	  time: selectedSlot?.label || "",
	}));
  };

  const handleFormChangeCheckLabour = (e) => {
	const { name, value } = e.target;

	if (name === "city") {
	  setSearchCriteria((prev) => ({
		...prev,
		city: value,
		area: "",
	  }));
	  setLabourResults([]);
	  setSearchError("");
	} else {
	  setSearchCriteria((prev) => ({
		...prev,
		[name]: value,
	  }));
	}
  };

  const handleSearchCheckLabour = async (e) => {
	e.preventDefault();

	const { gender, city, area } = searchCriteria;

	if (!gender || !city || !area) {
	  alert("Please fill all fields before searching");
	  return;
	}

	setSearchLoading(true);
	setSearchError("");
	setLabourResults([]);

	try {
	  const requestBody = {
		gender,
		city,
		area,
		service_type: "ac-repair",
	  };

	  const response = await fetch(`${BASE_URL}/api/search/labour-allservice/`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(requestBody),
	  });

	  const data = await response.json();
	  console.log("Search labour response:", data);

	  if (!response.ok) {
		throw new Error(data.error || "Failed to check labour");
	  }

	  const results = Array.isArray(data)
		? data
		: Array.isArray(data?.labours)
		? data.labours
		: Array.isArray(data?.data)
		? data.data
		: [];

	  setLabourResults(results);
	} catch (error) {
	  console.error("Request failed:", error);
	  setSearchError(error.message || "Request failed");
	  setLabourResults([]);
	} finally {
	  setSearchLoading(false);
	}
  };

  const handleScheduleChange = (e) => {
	const { name, value } = e.target;

	if (name === "city") {
	  setScheduleData((prev) => ({
		...prev,
		city: value,
		area: "",
	  }));
	} else {
	  setScheduleData((prev) => ({
		...prev,
		[name]: value,
	  }));
	}
  };

  const openFeedbackModal = async (service) => {
	setActiveFeedbackService(service);
	setFeedbackModalOpen(true);
	setFeedbackLoading(true);
	setFeedbackError("");
	setFeedbackData([]);

	try {
	  const response = await fetch(`${BASE_URL}/api/feedback/ac-repair`, {
		method: "GET",
		headers: {
		  Accept: "application/json",
		},
	  });

	  const result = await response.json();

	  if (!response.ok) {
		throw new Error(result.error || "Failed to fetch feedback");
	  }

	  const reviews = Array.isArray(result)
		? result
		: Array.isArray(result?.data)
		? result.data
		: Array.isArray(result?.reviews)
		? result.reviews
		: [];

	  const normalizedData = reviews
		.filter((item) => {
		  if (!service?.value) return true;
		  const serviceType = (
			item.service_type ||
			item.servicetype ||
			""
		  ).toLowerCase();
		  return (
			serviceType === service.value.toLowerCase() ||
			serviceType === service.label?.toLowerCase()
		  );
		})
		.map((item, index) => ({
		  id: item.id || `ac-repair-${index}`,
		  service_type:
			item.service_type || item.servicetype || "ac-repair",
		  rating: Number(item.rating) || 0,
		  feedback_data:
			item.feedback_data || item.feedback || item.comment || "",
		  name: item.name || item.user_name || item.username || "Anonymous User",
		}));

	  setFeedbackData(normalizedData);
	} catch (error) {
	  setFeedbackError(error.message || "Unable to fetch feedback");
	  setFeedbackData([]);
	} finally {
	  setFeedbackLoading(false);
	}
  };

  const closeFeedbackModal = () => {
	setFeedbackModalOpen(false);
	setFeedbackLoading(false);
	setFeedbackError("");
	setFeedbackData([]);
	setActiveFeedbackService(null);
  };

  const feedbackSummary = useMemo(() => {
	const totalReviews = feedbackData.length;

	if (totalReviews === 0) {
	  return {
		average: 0,
		totalReviews: 0,
		breakdown: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
	  };
	}

	const totalRating = feedbackData.reduce(
	  (sum, item) => sum + (Number(item.rating) || 0),
	  0
	);

	const breakdown = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };

	feedbackData.forEach((item) => {
	  const rating = Number(item.rating) || 0;
	  if (breakdown[rating] !== undefined) {
		breakdown[rating] += 1;
	  }
	});

	return {
	  average: totalRating / totalReviews,
	  totalReviews,
	  breakdown,
	};
  }, [feedbackData]);

  const maxBreakdownCount = Math.max(
	feedbackSummary.breakdown[5],
	feedbackSummary.breakdown[4],
	feedbackSummary.breakdown[3],
	feedbackSummary.breakdown[2],
	feedbackSummary.breakdown[1],
	1
  );

  const handleScheduleLabour = async (e) => {
	e.preventDefault();

	const { name, date, slot, city, area, mobile, address } = scheduleData;

	if (
	  !name ||
	  !date ||
	  !slot ||
	  !city ||
	  !area ||
	  !mobile ||
	  !address ||
	  selectedServices.length === 0
	) {
	  alert("Please fill all required fields and select at least one service");
	  return;
	}

	const selectedDate = new Date(date);
	const today = new Date();
	const maxDate = new Date();

	maxDate.setDate(maxDate.getDate() + scheduleDaysLimit);
	today.setHours(0, 0, 0, 0);
	maxDate.setHours(23, 59, 59, 999);

	if (selectedDate < today || selectedDate > maxDate) {
	  alert(`You can only schedule within today and next ${scheduleDaysLimit} days`);
	  return;
	}

	try {
	  const user = JSON.parse(localStorage.getItem("user"));

	  const requestBody = {
		...scheduleData,
		name: user?.name || scheduleData.name,
		mobile: user?.mobile || scheduleData.mobile,
		service_category: categoryKey,
		selected_city: getUserCity(),
		service: serviceKey,
		selected_services: selectedServices.map((service) => ({
		  value: service.value,
		  label: service.label,
		  price: cityPrice(service.value, service.price),
		  quantity: service.quantity,
		  unit: service.unit || "unit",
		  line_total: cityPrice(service.value, service.price) * service.quantity,
		})),
		total_items: totalItems,
		service_charge: total,
	  };

	  const response = await fetch(`${BASE_URL}/api/schedule/labour/`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(requestBody),
	  });

	  const data = await response.json();

	  if (!response.ok) {
		throw new Error(data.error || "Failed to schedule labour");
	  }

	  alert(data.message || "AC service scheduled successfully!");

	  setSelectedServices([]);
	  setScheduleData({
		name: "",
		date: "",
		time: "",
		slot: "",
		city: "",
		area: "",
		mobile: "",
		address: "",
		notes: "",
	  });
	} catch (error) {
	  alert(error.message || "Request failed");
	}
  };

  const getMinDate = () => new Date().toISOString().split("T")[0];

  const getMaxDate = () => {
	const max = new Date();
	max.setDate(max.getDate() + scheduleDaysLimit);
	return max.toISOString().split("T")[0];
  };

  const checkAreas = getCityAreas(searchCriteria.city);
  const scheduleAreas = getCityAreas(scheduleData.city);
  const selectedCheckCityLimit = getCityLimit(searchCriteria.city);
  const selectedScheduleCityLimit = getCityLimit(scheduleData.city);

  const styles = {
	page: {
	  minHeight: "100vh",
	  background: "linear-gradient(135deg, #f4f8ff 0%, #edf5ff 100%)",
	  padding: "24px 0 40px",
	  fontFamily: "Segoe UI, Tahoma, Geneva, Verdana, sans-serif",
	},
	container: {
	  width: "99%",
	  maxWidth: "1600px",
	  margin: "0 auto",
	},
	hero: {
	  background: "linear-gradient(135deg, #0f172a, #1d4ed8, #38bdf8)",
	  color: "#fff",
	  borderRadius: "26px",
	  padding: "30px",
	  boxShadow: "0 18px 42px rgba(29, 78, 216, 0.22)",
	  marginBottom: "24px",
	},
	badge: {
	  display: "inline-block",
	  padding: "7px 14px",
	  borderRadius: "999px",
	  background: "rgba(152, 142, 142, 0.15)",
	  fontSize: "13px",
	  fontWeight: "700",
	  marginBottom: "14px",
	},
	heroTitle: {
	  margin: 0,
	  fontSize: "2rem",
	  fontWeight: "800",
	  display: "flex",
	  alignItems: "center",
	  gap: "12px",
	  flexWrap: "wrap",
	  color: "#fff",
	},
	heroSub: {
	  marginTop: "10px",
	  marginBottom: 0,
	  color: "rgba(255,255,255,0.9)",
	  fontSize: "1rem",
	  maxWidth: "760px",
	},
	topBtn: {
	  border: "none",
	  borderRadius: "999px",
	  padding: "11px 18px",
	  fontWeight: "700",
	  background: "#fff",
	  color: "#1d4ed8",
	  boxShadow: "0 8px 20px rgba(255,255,255,0.18)",
	  cursor: "pointer",
	},
	summaryCard: {
	  background: "#fff",
	  borderRadius: "22px",
	  padding: "22px",
	  boxShadow: "0 10px 26px rgba(15, 23, 42, 0.08)",
	  border: "1px solid #eaf0f8",
	  height: "100%",
	},
	summaryTitle: {
	  fontSize: "15px",
	  fontWeight: "700",
	  color: "#475569",
	  marginBottom: "8px",
	},
	summaryValue: {
	  fontSize: "1.9rem",
	  fontWeight: "800",
	  color: "#111827",
	  marginBottom: "8px",
	},
	summaryText: {
	  margin: 0,
	  color: "#6b7280",
	  fontSize: "14px",
	},
	formCard: {
	  background: "#fff",
	  borderRadius: "26px",
	  boxShadow: "0 14px 34px rgba(15, 23, 42, 0.08)",
	  border: "1px solid #ebf1f8",
	  overflow: "hidden",
	  maxWidth: "1550px",
	  margin: "0 auto",
	},
	tabWrap: {
	  display: "flex",
	  gap: "12px",
	  padding: "20px 20px 0 20px",
	  flexWrap: "wrap",
	},
	tabBtn: (active) => ({
	  border: "none",
	  borderRadius: "999px",
	  padding: "12px 20px",
	  fontWeight: "700",
	  background: active
		? "linear-gradient(135deg, #1d4ed8, #2563eb)"
		: "#eef4ff",
	  color: active ? "#fff" : "#1d4ed8",
	  boxShadow: active ? "0 10px 24px rgba(37, 99, 235, 0.2)" : "none",
	  cursor: "pointer",
	}),
	body: {
	  padding: "32px 40px",
	},
	sectionTitle: {
	  fontSize: "1.5rem",
	  fontWeight: "800",
	  color: "#111827",
	  marginBottom: "6px",
	},
	sectionSub: {
	  color: "#6b7280",
	  marginBottom: "20px",
	  fontSize: "15px",
	},
	label: {
	  display: "block",
	  marginBottom: "8px",
	  fontWeight: "700",
	  color: "#334155",
	  fontSize: "14px",
	},
	input: {
	  width: "100%",
	  minWidth: "100%",
	  padding: "14px 16px",
	  borderRadius: "14px",
	  border: "1px solid #d6deeb",
	  outline: "none",
	  background: "#fff",
	  fontSize: "15px",
	  color: "#111827",
	  boxSizing: "border-box",
	},
	select: {
	  width: "100%",
	  minWidth: "100%",
	  padding: "14px 16px",
	  borderRadius: "14px",
	  border: "1px solid #d6deeb",
	  outline: "none",
	  background: "#fff",
	  fontSize: "15px",
	  color: "#111827",
	  boxSizing: "border-box",
	},
	textarea: {
	  width: "100%",
	  minWidth: "100%",
	  padding: "14px 16px",
	  borderRadius: "14px",
	  border: "1px solid #d6deeb",
	  outline: "none",
	  background: "#fff",
	  fontSize: "15px",
	  color: "#111827",
	  boxSizing: "border-box",
	  minHeight: "110px",
	  resize: "vertical",
	},
	fieldCard: {
	  background: "#f8fbff",
	  border: "1px solid #e6eef8",
	  borderRadius: "18px",
	  padding: "18px",
	  height: "100%",
	  minWidth: 0,
	  marginBottom: "16px",
	},
	iconBox: {
	  width: "44px",
	  height: "44px",
	  borderRadius: "12px",
	  display: "flex",
	  alignItems: "center",
	  justifyContent: "center",
	  background: "#eaf2ff",
	  color: "#1d4ed8",
	  marginBottom: "12px",
	  fontSize: "18px",
	},
	submitBtn: {
	  border: "none",
	  background: "linear-gradient(135deg, #1d4ed8, #2563eb)",
	  color: "#fff",
	  borderRadius: "999px",
	  padding: "13px 22px",
	  fontWeight: "800",
	  minWidth: "180px",
	  boxShadow: "0 10px 22px rgba(37,99,235,0.22)",
	  cursor: "pointer",
	},
	infoBox: {
	  background: "linear-gradient(135deg, #eff6ff, #f8fbff)",
	  border: "1px solid #dbeafe",
	  borderRadius: "18px",
	  padding: "16px 18px",
	  color: "#334155",
	  fontSize: "14px",
	  marginBottom: "22px",
	},
	priceBox: {
	  background: "linear-gradient(135deg, #ecfdf5, #f0fdf4)",
	  border: "1px solid #bbf7d0",
	  borderRadius: "16px",
	  padding: "16px 18px",
	  marginTop: "20px",
	  color: "#166534",
	},
	priceLabel: {
	  fontSize: "14px",
	  fontWeight: "700",
	  marginBottom: "6px",
	},
	priceValue: {
	  fontSize: "1.6rem",
	  fontWeight: "800",
	  margin: 0,
	  color: "#000",
	},
	priceNote: {
	  marginTop: "6px",
	  marginBottom: 0,
	  fontSize: "13px",
	  color: "#15803d",
	},
	emptyBox: {
	  background: "#fff",
	  border: "1px dashed #bfdbfe",
	  color: "#64748b",
	  borderRadius: "18px",
	  padding: "22px",
	  textAlign: "center",
	  marginTop: "22px",
	},
	serviceList: {
	  display: "flex",
	  flexDirection: "column",
	  gap: "22px",
	},
	serviceCard: {
	  background: "#fff",
	  border: "1px solid #dbeafe",
	  borderRadius: "24px",
	  overflow: "hidden",
	  boxShadow: "0 12px 28px rgba(15, 23, 42, 0.06)",
	},
	serviceInner: {
	  display: "grid",
	  gridTemplateColumns: "320px 1fr",
	  gap: "0",
	},
	imageWrap: {
	  position: "relative",
	  width: "100%",
	  height: "320px",
	  background: "#eaf2ff",
	  overflow: "hidden",
	  display: "flex",
	  alignItems: "center",
	  justifyContent: "center",
	},
	image: {
	  width: "100%",
	  height: "100%",
	  minHeight: "320px",
	  objectFit: "cover",
	  objectPosition: "center",
	  background: "#eaf2ff",
	  display: "block",
	},
	imageNavBtnLeft: {
	  position: "absolute",
	  top: "50%",
	  left: "12px",
	  transform: "translateY(-50%)",
	  width: "38px",
	  height: "38px",
	  borderRadius: "999px",
	  border: "none",
	  background: "rgba(15, 23, 42, 0.65)",
	  color: "#fff",
	  cursor: "pointer",
	  display: "flex",
	  alignItems: "center",
	  justifyContent: "center",
	  zIndex: 2,
	},
	imageNavBtnRight: {
	  position: "absolute",
	  top: "50%",
	  right: "12px",
	  transform: "translateY(-50%)",
	  width: "38px",
	  height: "38px",
	  borderRadius: "999px",
	  border: "none",
	  background: "rgba(15, 23, 42, 0.65)",
	  color: "#fff",
	  cursor: "pointer",
	  display: "flex",
	  alignItems: "center",
	  justifyContent: "center",
	  zIndex: 2,
	},
	imageDots: {
	  position: "absolute",
	  left: "50%",
	  bottom: "14px",
	  transform: "translateX(-50%)",
	  display: "flex",
	  gap: "8px",
	  zIndex: 2,
	},
	imageDot: {
	  width: "10px",
	  height: "10px",
	  borderRadius: "999px",
	  border: "none",
	  background: "rgba(255,255,255,0.55)",
	  cursor: "pointer",
	  padding: 0,
	},
	imageDotActive: {
	  width: "10px",
	  height: "10px",
	  borderRadius: "999px",
	  border: "none",
	  background: "#ffffff",
	  cursor: "pointer",
	  padding: 0,
	},
	imageCountBadge: {
	  position: "absolute",
	  top: "12px",
	  right: "12px",
	  background: "rgba(15, 23, 42, 0.72)",
	  color: "#fff",
	  padding: "6px 10px",
	  borderRadius: "999px",
	  fontSize: "12px",
	  fontWeight: "700",
	  zIndex: 2,
	},
	content: {
	  padding: "24px",
	},
	cardTop: {
	  display: "flex",
	  justifyContent: "space-between",
	  alignItems: "flex-start",
	  gap: "16px",
	  flexWrap: "wrap",
	},
	titleWrap: {
	  flex: 1,
	},
	serviceTitle: {
	  fontSize: "1.55rem",
	  fontWeight: "800",
	  color: "#111827",
	  marginBottom: "10px",
	  lineHeight: 1.3,
	},
	metaRow: {
	  display: "flex",
	  gap: "10px",
	  flexWrap: "wrap",
	  marginBottom: "12px",
	  alignItems: "center",
	},
	pill: {
	  background: "#eef4ff",
	  color: "#1d4ed8",
	  padding: "7px 12px",
	  borderRadius: "999px",
	  fontSize: "13px",
	  fontWeight: "700",
	},
	feedbackBtn: {
	  border: "1px solid #c7d7fe",
	  background: "#fff",
	  color: "#1d4ed8",
	  padding: "7px 12px",
	  borderRadius: "999px",
	  fontSize: "13px",
	  fontWeight: "700",
	  cursor: "pointer",
	  display: "inline-flex",
	  alignItems: "center",
	  gap: "8px",
	},
	price: {
	  fontSize: "2rem",
	  fontWeight: "900",
	  color: "#1d4ed8",
	  whiteSpace: "nowrap",
	},
	infoGrid: {
	  display: "grid",
	  gridTemplateColumns: "1fr 1fr",
	  gap: "16px",
	  marginTop: "18px",
	},
	infoCard: {
	  background: "#f8fbff",
	  border: "1px solid #e6eef8",
	  borderRadius: "16px",
	  padding: "16px",
	},
	infoTitleGreen: {
	  display: "flex",
	  alignItems: "center",
	  gap: "8px",
	  fontSize: "15px",
	  fontWeight: "800",
	  marginBottom: "10px",
	  color: "#15803d",
	},
	infoTitleRed: {
	  display: "flex",
	  alignItems: "center",
	  gap: "8px",
	  fontSize: "15px",
	  fontWeight: "800",
	  marginBottom: "10px",
	  color: "#dc2626",
	},
	list: {
	  margin: 0,
	  paddingLeft: "18px",
	  color: "#475569",
	  fontSize: "14px",
	  lineHeight: 1.8,
	},
	bottomRow: {
	  display: "flex",
	  justifyContent: "space-between",
	  alignItems: "center",
	  gap: "18px",
	  flexWrap: "wrap",
	  marginTop: "22px",
	  paddingTop: "18px",
	  borderTop: "1px solid #e2e8f0",
	},
	qtyWrap: {
	  display: "flex",
	  alignItems: "center",
	  gap: "12px",
	  flexWrap: "wrap",
	},
	qtyBtn: {
	  width: "42px",
	  height: "42px",
	  border: "none",
	  borderRadius: "12px",
	  background: "linear-gradient(135deg, #1d4ed8, #2563eb)",
	  color: "#fff",
	  fontSize: "18px",
	  fontWeight: "800",
	  cursor: "pointer",
	  display: "flex",
	  alignItems: "center",
	  justifyContent: "center",
	},
	qtyBtnDisabled: {
	  width: "42px",
	  height: "42px",
	  border: "none",
	  borderRadius: "12px",
	  background: "#cbd5e1",
	  color: "#64748b",
	  fontSize: "18px",
	  fontWeight: "800",
	  cursor: "not-allowed",
	  display: "flex",
	  alignItems: "center",
	  justifyContent: "center",
	},
	qtyValue: {
	  minWidth: "42px",
	  textAlign: "center",
	  fontSize: "20px",
	  fontWeight: "800",
	  color: "#0f172a",
	},
	qtyNote: {
	  fontSize: "13px",
	  color: "#64748b",
	  marginLeft: "10px",
	},
	lineTotalWrap: {
	  textAlign: "right",
	},
	lineTotalLabel: {
	  fontSize: "13px",
	  color: "#64748b",
	  marginBottom: "4px",
	},
	lineTotalValue: {
	  fontSize: "1.35rem",
	  fontWeight: "900",
	  color: "#111827",
	},

	resultHeader: {
	  display: "flex",
	  justifyContent: "space-between",
	  alignItems: "center",
	  gap: "16px",
	  flexWrap: "wrap",
	  marginTop: "28px",
	  marginBottom: "18px",
	},
	resultTitle: {
	  margin: 0,
	  fontSize: "1.7rem",
	  fontWeight: "900",
	  color: "#0f172a",
	},
	resultSub: {
	  margin: "6px 0 0 0",
	  fontSize: "14px",
	  color: "#64748b",
	},
	resultCountBadge: {
	  background: "linear-gradient(135deg, #1d4ed8, #2563eb)",
	  color: "#fff",
	  padding: "10px 16px",
	  borderRadius: "999px",
	  fontSize: "13px",
	  fontWeight: "800",
	  boxShadow: "0 10px 20px rgba(37, 99, 235, 0.18)",
	},
	labourGrid: {
	  display: "grid",
	  gridTemplateColumns: "1fr",
	  gap: "18px",
	},
	labourCard: {
	  background: "#ffffff",
	  border: "1px solid #e2e8f0",
	  borderRadius: "24px",
	  padding: "22px",
	  boxShadow: "0 10px 28px rgba(15, 23, 42, 0.06)",
	  transition: "all 0.3s ease",
	},
	labourCardTop: {
	  display: "flex",
	  alignItems: "center",
	  justifyContent: "space-between",
	  gap: "16px",
	  flexWrap: "wrap",
	  marginBottom: "14px",
	},
	labourProfileWrap: {
	  display: "flex",
	  alignItems: "center",
	  gap: "16px",
	  flex: 1,
	  minWidth: 0,
	},
	labourAvatarLarge: {
	  width: "74px",
	  height: "74px",
	  minWidth: "74px",
	  borderRadius: "22px",
	  display: "flex",
	  alignItems: "center",
	  justifyContent: "center",
	  background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
	  color: "#1d4ed8",
	  fontSize: "28px",
	  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.75)",
	},
	labourTextWrap: {
	  minWidth: 0,
	  flex: 1,
	},
	labourNameBlock: {
	  fontSize: "1.4rem",
	  fontWeight: "900",
	  color: "#0f172a",
	  lineHeight: 1.25,
	  marginBottom: "6px",
	  whiteSpace: "normal",
	  wordBreak: "normal",
	  overflowWrap: "break-word",
	},
	labourRoleText: {
	  fontSize: "14px",
	  color: "#64748b",
	  lineHeight: 1.6,
	  margin: 0,
	},
	availabilityPill: {
	  background: "#dcfce7",
	  color: "#166534",
	  border: "1px solid #bbf7d0",
	  padding: "8px 14px",
	  borderRadius: "999px",
	  fontSize: "13px",
	  fontWeight: "800",
	  whiteSpace: "nowrap",
	},
	labourDivider: {
	  height: "1px",
	  background: "#edf2f7",
	  margin: "16px 0",
	},
	labourMetaGrid: {
	  display: "flex",
	  flexWrap: "wrap",
	  gap: "12px",
	  marginBottom: "18px",
	},
	labourChipBlue: {
	  background: "#eff6ff",
	  color: "#1d4ed8",
	  padding: "10px 14px",
	  borderRadius: "999px",
	  fontSize: "13px",
	  fontWeight: "700",
	  border: "1px solid #dbeafe",
	},
	labourChipGray: {
	  background: "#f8fafc",
	  color: "#334155",
	  padding: "10px 14px",
	  borderRadius: "999px",
	  fontSize: "13px",
	  fontWeight: "700",
	  border: "1px solid #e2e8f0",
	},
	labourChipLight: {
	  background: "#ffffff",
	  color: "#475569",
	  padding: "10px 14px",
	  borderRadius: "999px",
	  fontSize: "13px",
	  fontWeight: "700",
	  border: "1px solid #e2e8f0",
	},
	labourFooter: {
	  display: "flex",
	  justifyContent: "space-between",
	  alignItems: "center",
	  gap: "14px",
	  flexWrap: "wrap",
	},
	labourFooterText: {
	  fontSize: "14px",
	  color: "#64748b",
	  margin: 0,
	},
	labourActionBtn: {
	  border: "none",
	  background: "linear-gradient(135deg, #10b981, #059669)",
	  color: "#fff",
	  borderRadius: "999px",
	  padding: "12px 18px",
	  fontWeight: "800",
	  fontSize: "14px",
	  cursor: "pointer",
	  display: "inline-flex",
	  alignItems: "center",
	  gap: "8px",
	  boxShadow: "0 10px 20px rgba(16, 185, 129, 0.18)",
	},

	modalOverlay: {
	  position: "fixed",
	  inset: 0,
	  background: "rgba(15, 23, 42, 0.58)",
	  display: "flex",
	  alignItems: "center",
	  justifyContent: "center",
	  zIndex: 9999,
	  padding: "20px",
	},
	modalCard: {
	  width: "100%",
	  maxWidth: "760px",
	  maxHeight: "88vh",
	  overflowY: "auto",
	  background: "#fff",
	  borderRadius: "24px",
	  padding: 0,
	  boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
	},
	modalHeader: {
	  display: "flex",
	  justifyContent: "space-between",
	  alignItems: "flex-start",
	  gap: "12px",
	  padding: "22px 22px 16px",
	  borderBottom: "1px solid #e5e7eb",
	  position: "sticky",
	  top: 0,
	  background: "#fff",
	  zIndex: 2,
	},
	modalTitle: {
	  margin: 0,
	  fontSize: "1.25rem",
	  fontWeight: "900",
	  color: "#111827",
	},
	modalSub: {
	  marginTop: "6px",
	  fontSize: "14px",
	  color: "#64748b",
	},
	modalCloseBtn: {
	  width: "42px",
	  height: "42px",
	  borderRadius: "999px",
	  border: "none",
	  background: "#f3f4f6",
	  color: "#111827",
	  cursor: "pointer",
	  display: "flex",
	  alignItems: "center",
	  justifyContent: "center",
	},
	modalBody: {
	  padding: "22px",
	},
	ratingBig: {
	  fontSize: "2.8rem",
	  fontWeight: "900",
	  color: "#111827",
	  display: "flex",
	  alignItems: "center",
	  gap: "8px",
	  marginBottom: "6px",
	},
	reviewsCount: {
	  color: "#4b5563",
	  fontSize: "15px",
	  marginBottom: "22px",
	},
	ratingRow: {
	  display: "grid",
	  gridTemplateColumns: "50px 1fr 70px",
	  gap: "12px",
	  alignItems: "center",
	  marginBottom: "14px",
	},
	ratingTrack: {
	  height: "8px",
	  background: "#e5e7eb",
	  borderRadius: "999px",
	  overflow: "hidden",
	},
	ratingFill: {
	  height: "100%",
	  background: "#111827",
	  borderRadius: "999px",
	},
	allReviewsTitle: {
	  fontSize: "1.6rem",
	  fontWeight: "900",
	  color: "#111827",
	  marginTop: "28px",
	  marginBottom: "16px",
	},
	reviewCard: {
	  background: "#fff",
	  border: "1px solid #e5e7eb",
	  borderRadius: "16px",
	  padding: "16px",
	  marginBottom: "14px",
	},
	reviewHeader: {
	  display: "flex",
	  justifyContent: "space-between",
	  alignItems: "flex-start",
	  gap: "12px",
	  marginBottom: "10px",
	  flexWrap: "wrap",
	},
	reviewName: {
	  fontSize: "1rem",
	  fontWeight: "800",
	  color: "#111827",
	  margin: 0,
	},
	reviewRatingBadge: {
	  background: "#15803d",
	  color: "#fff",
	  borderRadius: "10px",
	  padding: "6px 10px",
	  fontSize: "14px",
	  fontWeight: "800",
	  display: "inline-flex",
	  alignItems: "center",
	  gap: "6px",
	},
	reviewComment: {
	  margin: 0,
	  fontSize: "15px",
	  color: "#475569",
	  lineHeight: 1.7,
	},
	loadingBox: {
	  textAlign: "center",
	  padding: "28px",
	  color: "#1d4ed8",
	  fontWeight: "700",
	},
	errorBox: {
	  textAlign: "center",
	  padding: "20px",
	  borderRadius: "14px",
	  background: "#fef2f2",
	  border: "1px solid #fecaca",
	  color: "#b91c1c",
	  fontWeight: "700",
	  marginTop: "18px",
	},
	noReviewBox: {
	  textAlign: "center",
	  padding: "20px",
	  borderRadius: "14px",
	  background: "#f8fafc",
	  border: "1px solid #e2e8f0",
	  color: "#64748b",
	},
  };

  return (
	<div style={styles.page}>
	  <div style={styles.container}>
		<div style={styles.hero}>
		  <div className="row align-items-center g-3">
			<div className="col-lg-8">
			  <span style={styles.badge}>Service Booking</span>
			  <h2 style={styles.heroTitle}>
				<FaSnowflake />
				{serviceLabel} Labour Services
			  </h2>
			  <p style={styles.heroSub}>
				Search available labour by city and area, or schedule multiple{" "}
				{serviceLabel.toLowerCase()} services for a selected date and time.
			  </p>
			</div>

			<div className="col-lg-4 text-lg-end">
			  <button style={styles.topBtn} onClick={() => navigate(-1)}>
				<FaArrowLeft className="me-2" />
				Back
			  </button>
			</div>
		  </div>
		</div>

		<div className="row g-4 mb-4">
		  <div className="col-md-4">
			<div style={styles.summaryCard}>
			  <div style={styles.summaryTitle}>Service Category</div>
			  <div style={styles.summaryValue}>{serviceLabel}</div>
			  <p style={styles.summaryText}>
				Fast booking flow for multiple {serviceLabel.toLowerCase()} requirements.
			  </p>
			</div>
		  </div>

		  <div className="col-md-4">
			<div style={styles.summaryCard}>
			  <div style={styles.summaryTitle}>Available Services</div>
			  <div style={styles.summaryValue}>{enhancedServices.length}</div>
			  <p style={styles.summaryText}>
				Config-driven AC service options with quantity-based booking.
			  </p>
			</div>
		  </div>

		  <div className="col-md-4">
			<div style={styles.summaryCard}>
			  <div style={styles.summaryTitle}>Location Source</div>
			  <div style={styles.summaryValue}>{categoryLabel}</div>
			  <p style={styles.summaryText}>
				Cities and areas are loaded dynamically from config.
			  </p>
			</div>
		  </div>
		</div>

		<div style={styles.formCard}>
		  <div style={styles.tabWrap}>
			<button
			  type="button"
			  style={styles.tabBtn(activeForm === "check")}
			  onClick={() => setActiveForm("check")}
			>
			  <FaSearch className="me-2" />
			  Check Labour
			</button>

			<button
			  type="button"
			  style={styles.tabBtn(activeForm === "schedule")}
			  onClick={() => setActiveForm("schedule")}
			>
			  <FaCalendarCheck className="me-2" />
			  Schedule Labour
			</button>
		  </div>

		  <div style={styles.body}>
			{activeForm === "check" ? (
			  <form onSubmit={handleSearchCheckLabour}>
				<h3 style={styles.sectionTitle}>Check Labour Availability</h3>
				<p style={styles.sectionSub}>
				  Select preferred gender, city, and work area to check labour
				  availability for {serviceLabel.toLowerCase()} service.
				</p>

				<div style={styles.infoBox}>
				  Use the filters below to search labour availability by gender,
				  city, and working area.
				  {selectedCheckCityLimit !== null && searchCriteria.city && (
					<div className="mt-2">City limit: {selectedCheckCityLimit}</div>
				  )}
				</div>

				<div className="row g-4">
				  <div className="col-md-4">
					<div style={styles.fieldCard}>
					  <div style={styles.iconBox}>
						<FaUserFriends />
					  </div>
					  <label style={styles.label}>Gender</label>
					  <select
						name="gender"
						value={searchCriteria.gender}
						onChange={handleFormChangeCheckLabour}
						required
						style={styles.select}
					  >
						<option value="">Select</option>
						<option value="Male">Male</option>
						<option value="Female">Female</option>
					  </select>
					</div>
				  </div>

				  <div className="col-md-4">
					<div style={styles.fieldCard}>
					  <div style={styles.iconBox}>
						<FaCity />
					  </div>
					  <label style={styles.label}>City</label>
					  <select
						name="city"
						value={searchCriteria.city}
						onChange={handleFormChangeCheckLabour}
						required
						style={styles.select}
					  >
						<option value="">Select City</option>
						{Object.keys(areaOptions).map((city) => (
						  <option key={city} value={city}>
							{city.charAt(0).toUpperCase() + city.slice(1)}
						  </option>
						))}
					  </select>
					</div>
				  </div>

				  <div className="col-md-4">
					<div style={styles.fieldCard}>
					  <div style={styles.iconBox}>
						<FaMapMarkerAlt />
					  </div>
					  <label style={styles.label}>Working Area</label>
					  <select
						name="area"
						value={searchCriteria.area}
						onChange={handleFormChangeCheckLabour}
						required
						style={styles.select}
					  >
						<option value="">Select Area</option>
						{checkAreas.map((area) => (
						  <option key={area} value={area}>
							{area}
						  </option>
						))}
					  </select>
					</div>
				  </div>
				</div>

				<div className="mt-4">
				  <button type="submit" style={styles.submitBtn}>
					<FaSearch className="me-2" />
					{searchLoading ? "Searching..." : "Search Labour"}
				  </button>
				</div>

				{searchError && <div style={styles.errorBox}>{searchError}</div>}

				{!searchLoading && !searchError && labourResults.length > 0 && (
				  <div style={{ marginTop: "26px" }}>
					<div style={styles.resultHeader}>
					  <div>
						<h4 style={styles.resultTitle}>Available Labour</h4>
						<p style={styles.resultSub}>
						  Matching labour profiles for your selected city and area.
						</p>
					  </div>

					  <div style={styles.resultCountBadge}>
						{labourResults.length} Found
					  </div>
					</div>

					<div style={styles.labourGrid}>
					  {labourResults.map((labour, index) => (
						<div key={labour._id || index} style={styles.labourCard}>
						  <div style={styles.labourCardTop}>
							<div style={styles.labourProfileWrap}>
							  <div style={styles.labourAvatarLarge}>
								<FaUser />
							  </div>

							  <div style={styles.labourTextWrap}>
								<div style={styles.labourNameBlock}>
								  {labour.labour_name || "Unnamed Labour"}
								</div>
								<p style={styles.labourRoleText}>
								  Experienced labour available for AC repair support in your selected area.
								</p>
							  </div>
							</div>

							<div style={styles.availabilityPill}>Available</div>
						  </div>

						  <div style={styles.labourDivider}></div>

						  <div style={styles.labourMetaGrid}>
							<span style={styles.labourChipBlue}>
							  Gender: {labour.gender || "N/A"}
							</span>

							<span style={styles.labourChipGray}>
							  Age: {labour.age || "N/A"}
							</span>

							{searchCriteria.city && (
							  <span style={styles.labourChipLight}>
								City: {searchCriteria.city}
							  </span>
							)}

							{searchCriteria.area && (
							  <span style={styles.labourChipLight}>
								Area: {searchCriteria.area}
							  </span>
							)}
						  </div>

						  <div style={styles.labourFooter}>
							<p style={styles.labourFooterText}>
							  Matching profile for your selected labour search.
							</p>

							<button type="button" style={styles.labourActionBtn}>
							  Continue
							  <FaArrowRight />
							</button>
						  </div>
						</div>
					  ))}
					</div>
				  </div>
				)}

				{!searchLoading &&
				  !searchError &&
				  labourResults.length === 0 &&
				  searchCriteria.gender &&
				  searchCriteria.city &&
				  searchCriteria.area && (
					<div style={styles.emptyBox}>
					  <div
						style={{
						  fontSize: "1.1rem",
						  fontWeight: "800",
						  marginBottom: "8px",
						  color: "#0f172a",
						}}
					  >
						No labour found
					  </div>
					  <div style={{ fontSize: "14px", color: "#64748b" }}>
						Try changing gender, city, or working area to see more available labour.
					  </div>
					</div>
				  )}
			  </form>
			) : (
			  <form
				onSubmit={handleScheduleLabour}
				style={{ maxWidth: "1250px", margin: "0 auto" }}
			  >
				<h3 style={styles.sectionTitle}>Schedule Labour</h3>
				<p style={styles.sectionSub}>
				  Select AC services directly, increase quantity where needed,
				  view customer feedback, and review the final cost summary before booking.
				</p>

				<div style={styles.infoBox}>
				  You can schedule only for today and the next {scheduleDaysLimit} days.
				  {selectedScheduleCityLimit !== null && scheduleData.city && (
					<div className="mt-2">City limit: {selectedScheduleCityLimit}</div>
				  )}
				</div>

				<div style={styles.serviceList}>
				  {enhancedServices.map((service) => {
					const selected = getSelectedService(service.value);
					const qty = selected?.quantity || 0;
					const reachedMax = qty >= (service.limit || 10);
					const lineTotal = qty * cityPrice(service.value, service.price);

					const currentImageIndex = getCurrentImageIndex(
					  service.value,
					  service.images.length
					);
					const currentImage = service.images[currentImageIndex];

					return (
					  <div key={service.value} style={styles.serviceCard}>
						<div className="ac-service-inner" style={styles.serviceInner}>
						  <div style={styles.imageWrap}>
							<img
							  src={currentImage}
							  alt={service.label}
							  style={styles.image}
							/>

							{service.images.length > 1 && (
							  <>
								<button
								  type="button"
								  style={styles.imageNavBtnLeft}
								  onClick={() =>
									goToPrevImage(service.value, service.images.length)
								  }
								  aria-label={`Previous image for ${service.label}`}
								>
								  <FaChevronLeft />
								</button>

								<button
								  type="button"
								  style={styles.imageNavBtnRight}
								  onClick={() =>
									goToNextImage(service.value, service.images.length)
								  }
								  aria-label={`Next image for ${service.label}`}
								>
								  <FaChevronRight />
								</button>

								<div style={styles.imageCountBadge}>
								  {currentImageIndex + 1}/{service.images.length}
								</div>

								<div style={styles.imageDots}>
								  {service.images.map((_, index) => (
									<button
									  key={`${service.value}-dot-${index}`}
									  type="button"
									  onClick={() => setImageAtIndex(service.value, index)}
									  style={
										index === currentImageIndex
										  ? styles.imageDotActive
										  : styles.imageDot
									  }
									  aria-label={`Go to image ${index + 1} for ${service.label}`}
									/>
								  ))}
								</div>
							  </>
							)}
						  </div>

						  <div style={styles.content}>
							<div style={styles.cardTop}>
							  <div style={styles.titleWrap}>
								<div style={styles.serviceTitle}>{service.label}</div>

								<div style={styles.metaRow}>
								  <span style={styles.pill}>
									Unit: {service.unit || "unit"}
								  </span>
								  <span style={styles.pill}>
									Max limit: {service.limit || 10}
								  </span>

								  <button
									type="button"
									style={styles.feedbackBtn}
									onClick={() => openFeedbackModal(service)}
								  >
									<FaCommentDots />
									View Feedback
								  </button>
								</div>
							  </div>

							  <div style={styles.price}>₹ {cityPrice(service.value, service.price)}</div>
							</div>

							<div className="ac-service-info-grid" style={styles.infoGrid}>
							  <div style={styles.infoCard}>
								<div style={styles.infoTitleGreen}>
								  <FaCheckCircle />
								  What we do
								</div>
								<ul style={styles.list}>
								  {service.dos.map((point, i) => (
									<li key={i}>{point}</li>
								  ))}
								</ul>
							  </div>

							  <div style={styles.infoCard}>
								<div style={styles.infoTitleRed}>
								  <FaTimesCircle />
								  What we do not do
								</div>
								<ul style={styles.list}>
								  {service.donts.map((point, i) => (
									<li key={i}>{point}</li>
								  ))}
								</ul>
							  </div>
							</div>

							<div style={styles.bottomRow}>
							  <div>
								<div style={styles.qtyWrap}>
								  <button
									style={
									  qty === 0 ? styles.qtyBtnDisabled : styles.qtyBtn
									}
									onClick={() =>
									  qty > 0 &&
									  updateQty(service.value, -1, service.limit)
									}
									disabled={qty === 0}
									type="button"
								  >
									<FaMinus />
								  </button>

								  <div style={styles.qtyValue}>
									{qty > 0 ? fmtTime(qty) : "—"}
								  </div>

								  <button
									style={
									  reachedMax ? styles.qtyBtnDisabled : styles.qtyBtn
									}
									onClick={() => addService(service)}
									disabled={reachedMax}
									type="button"
								  >
									<FaPlus />
								  </button>

								  <span style={styles.qtyNote}>
									+30 min per click · max {fmtTime(service.limit || 10)}
								  </span>
								</div>
							  </div>

							  <div style={styles.lineTotalWrap}>
								<div style={styles.lineTotalLabel}>Selected total</div>
								<div style={styles.lineTotalValue}>₹ {lineTotal}</div>
							  </div>
							</div>
						  </div>
						</div>
					  </div>
					);
				  })}
				</div>

				<div className="row g-4 mt-2">
				  <div className="col-md-4">
					<div style={styles.fieldCard}>
					  <div style={styles.iconBox}>
						<FaUser />
					  </div>
					  <label style={styles.label}>Name</label>
					  <input
						type="text"
						name="name"
						value={scheduleData.name}
						onChange={handleScheduleChange}
						placeholder="Enter your name"
						required
						style={styles.input}
					  />
					</div>
				  </div>

				  <div className="col-md-4">
					<div style={styles.fieldCard}>
					  <div style={styles.iconBox}>
						<FaCalendarCheck />
					  </div>
					  <label style={styles.label}>Date</label>
					  <input
						type="date"
						name="date"
						value={scheduleData.date}
						min={getMinDate()}
						max={getMaxDate()}
						onChange={handleScheduleChange}
						required
						style={styles.input}
					  />
					</div>
				  </div>

				  <div className="col-md-4">
					<div style={styles.fieldCard}>
					  <div style={styles.iconBox}>
						<FaClock />
					  </div>
					  <label style={styles.label}>Time Slot</label>
					  <select
						name="slot"
						value={scheduleData.slot}
						onChange={handleTimeSlotChange}
						required
						style={styles.select}
					  >
						<option value="">Select Time Slot</option>
						{timeSlots.map((item) => (
						  <option key={item.slot} value={item.slot}>
							{item.label}
						  </option>
						))}
					  </select>
					</div>
				  </div>

				  <div className="col-md-4">
					<div style={styles.fieldCard}>
					  <div style={styles.iconBox}>
						<FaCity />
					  </div>
					  <label style={styles.label}>City</label>
					  <select
						name="city"
						value={scheduleData.city}
						onChange={handleScheduleChange}
						required
						style={styles.select}
					  >
						<option value="">Select City</option>
						{Object.keys(areaOptions).map((city) => (
						  <option key={city} value={city}>
							{city.charAt(0).toUpperCase() + city.slice(1)}
						  </option>
						))}
					  </select>
					</div>
				  </div>

				  <div className="col-md-4">
					<div style={styles.fieldCard}>
					  <div style={styles.iconBox}>
						<FaMapMarkerAlt />
					  </div>
					  <label style={styles.label}>Working Area</label>
					  <select
						name="area"
						value={scheduleData.area}
						onChange={handleScheduleChange}
						required
						style={styles.select}
					  >
						<option value="">Select Area</option>
						{scheduleAreas.map((area) => (
						  <option key={area} value={area}>
							{area}
						  </option>
						))}
					  </select>
					</div>
				  </div>

				  <div className="col-md-4">
					<div style={styles.fieldCard}>
					  <div style={styles.iconBox}>
						<FaPhoneAlt />
					  </div>
					  <label style={styles.label}>Mobile Number</label>
					  <input
						type="tel"
						name="mobile"
						value={scheduleData.mobile}
						onChange={handleScheduleChange}
						placeholder="Enter mobile number"
						pattern="[0-9]{10}"
						minLength={10}
						maxLength={10}
						required
						style={styles.input}
					  />
					</div>
				  </div>

				  <div className="col-md-6">
					<div style={styles.fieldCard}>
					  <div style={styles.iconBox}>
						<FaMapMarkerAlt />
					  </div>
					  <label style={styles.label}>Address</label>
					  <input
						type="text"
						name="address"
						value={scheduleData.address}
						onChange={handleScheduleChange}
						placeholder="Enter full address"
						required
						style={styles.input}
					  />
					</div>
				  </div>

				  <div className="col-md-6">
					<div style={styles.fieldCard}>
					  <div style={styles.iconBox}>
						<FaTools />
					  </div>
					  <label style={styles.label}>Notes</label>
					  <textarea
						name="notes"
						value={scheduleData.notes}
						onChange={handleScheduleChange}
						placeholder="Extra service details"
						style={styles.textarea}
					  />
					</div>
				  </div>
				</div>

				{selectedServices.length > 0 ? (
				  <div style={styles.priceBox}>
					<div style={styles.priceLabel}>Booking Summary</div>

					{selectedServices.map((service) => (
					  <div key={service.value} style={{ marginBottom: "8px", color: "#000" }}>
						{service.label} — {fmtTime(service.quantity)} × ₹{cityPrice(service.value, service.price)} = ₹{cityPrice(service.value, service.price) * service.quantity}
					  </div>
					))}

					<p style={styles.priceValue}>Total: ₹ {total}</p>
					<p style={styles.priceNote}>
					  Total charge based on selected AC services
					</p>

					<div className="mt-4">
					  <button type="submit" style={styles.submitBtn}>
						<FaCalendarCheck className="me-2" />
						Schedule Service
					  </button>
					</div>
				  </div>
				) : (
				  <div style={styles.emptyBox}>
					No service selected yet. Use the + button to add AC services.
				  </div>
				)}
			  </form>
			)}
		  </div>
		</div>
	  </div>

	  {feedbackModalOpen && (
		<div style={styles.modalOverlay} onClick={closeFeedbackModal}>
		  <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
			<div style={styles.modalHeader}>
			  <div>
				<h3 style={styles.modalTitle}>Service Feedback</h3>
				<div style={styles.modalSub}>
				  {activeFeedbackService?.label || "AC Repair"} (
				  {activeFeedbackService?.value || "ac-repair"})
				</div>
			  </div>

			  <button
				type="button"
				style={styles.modalCloseBtn}
				onClick={closeFeedbackModal}
			  >
				<FaTimes />
			  </button>
			</div>

			<div style={styles.modalBody}>
			  {feedbackLoading ? (
				<div style={styles.loadingBox}>Loading feedback...</div>
			  ) : feedbackError ? (
				<div style={styles.errorBox}>{feedbackError}</div>
			  ) : feedbackData.length === 0 ? (
				<div style={styles.noReviewBox}>
				  No feedback available for this service.
				</div>
			  ) : (
				<>
				  <div style={styles.ratingBig}>
					<FaStar />
					{feedbackSummary.average.toFixed(2)}
				  </div>

				  <div style={styles.reviewsCount}>
					{feedbackSummary.totalReviews} review
					{feedbackSummary.totalReviews > 1 ? "s" : ""}
				  </div>

				  {[5, 4, 3, 2, 1].map((star) => {
					const count = feedbackSummary.breakdown[star];
					const width = `${(count / maxBreakdownCount) * 100}%`;

					return (
					  <div key={star} style={styles.ratingRow}>
						<div>
						  <FaStar style={{ marginRight: "6px" }} />
						  {star}
						</div>

						<div style={styles.ratingTrack}>
						  <div style={{ ...styles.ratingFill, width }} />
						</div>

						<div>{count}</div>
					  </div>
					);
				  })}

				  <div style={styles.allReviewsTitle}>All reviews</div>

				  {feedbackData.map((review, index) => (
					<div
					  key={review.id || `review-${index}`}
					  style={styles.reviewCard}
					>
					  <div style={styles.reviewHeader}>
						<div>
						  <p style={styles.reviewName}>
							{review.name || "Anonymous User"}
						  </p>
						</div>

						<div style={styles.reviewRatingBadge}>
						  <FaStar />
						  {Number(review.rating) || 0}
						</div>
					  </div>

					  <p style={styles.reviewComment}>
						{review.feedback_data || "No review text available."}
					  </p>
					</div>
				  ))}
				</>
			  )}
			</div>
		  </div>
		</div>
	  )}

	  <style>
		{`
		  @media (max-width: 900px) {
			.ac-service-inner {
			  grid-template-columns: 1fr !important;
			}

			.ac-service-info-grid {
			  grid-template-columns: 1fr !important;
			}
		  }

		  @media (max-width: 768px) {
			.labour-card-top-mobile {
			  flex-direction: column !important;
			  align-items: flex-start !important;
			}
		  }

		  @media (max-width: 576px) {
			.ac-service-inner img {
			  min-height: 220px !important;
			}
		  }
		`}
	  </style>
	</div>
  );
};

export default AcRepair;